import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Filter } from 'lucide-react';

const PROVINCES = ['SAN_JOSE', 'ALAJUELA', 'HEREDIA', 'CARTAGO', 'GUANACASTE', 'LIMON'];
const CONDITIONS = ['NEW', 'USED'];
const SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

export default function Accessories() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [showFilters, setShowFilters] = useState(false);
  const [subcategories, setSubcategories] = useState([]);

  const filters = {
    search: searchParams.get('search') || '',
    subcategory: searchParams.get('subcategory') || '',
    condition: searchParams.get('condition') || '',
    min_price: searchParams.get('min_price') || '',
    max_price: searchParams.get('max_price') || '',
    province: searchParams.get('province') || '',
    sort: searchParams.get('sort') || 'newest',
    color: searchParams.get('color') || '',
    size: searchParams.get('size') || ''
  };

  useEffect(() => {
    // Fetch subcategories from API
    fetch('/api/accessories/subcategories')
      .then(r => r.json())
      .then(data => setSubcategories(Array.isArray(data) ? data : []))
      .catch(() => setSubcategories([]));
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [searchParams]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      // Read current filters from URL params
      const currentFilters = {
        search: searchParams.get('search') || '',
        subcategory: searchParams.get('subcategory') || '',
        condition: searchParams.get('condition') || '',
        min_price: searchParams.get('min_price') || '',
        max_price: searchParams.get('max_price') || '',
        province: searchParams.get('province') || '',
        sort: searchParams.get('sort') || 'newest',
        color: searchParams.get('color') || '',
        size: searchParams.get('size') || ''
      };
      
      const params = new URLSearchParams({ is_accessory: 'true' });
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const response = await fetch(`/api/products?${params.toString()}`);
      const data = await response.json();
      setProducts(data.products || []);
      setPagination(data.pagination || { page: 1, pages: 1, total: data.products?.length || 0 });
    } catch (err) {
      console.error('Error fetching accessories:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) newParams.set(key, value);
    else newParams.delete(key);
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const clearFilters = () => setSearchParams({});

  return (
    <div className="min-h-screen">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl md:text-4xl font-black text-white uppercase tracking-tight">
                🧤 Accesorios para Moto
              </h1>
              <p className="text-gray-300 mt-2">Cascos, guantes, chaquetas y más</p>
            </div>
            <div className="hidden md:flex gap-2">
              {subcategories.map(sub => (
                <button
                  key={sub.id}
                  onClick={() => updateFilter('subcategory', sub.id)}
                  className={`px-4 py-2 rounded-full text-sm font-bold transition ${
                    filters.subcategory === sub.id 
                      ? 'bg-primary text-white' 
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                >
                  {sub.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Subcategory Pills Mobile */}
      <div className="md:hidden bg-white border-b p-4">
        <div className="flex gap-2 overflow-x-auto pb-2">
          <button
            onClick={() => updateFilter('subcategory', '')}
            className={`shrink-0 px-4 py-2 rounded-full text-sm font-bold ${
              !filters.subcategory ? 'bg-black text-white' : 'bg-gray-100'
            }`}
          >
            Todos
          </button>
          {subcategories.map(sub => (
            <button
              key={sub.id}
              onClick={() => updateFilter('subcategory', sub.id)}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-bold ${
                filters.subcategory === sub.id ? 'bg-black text-white' : 'bg-gray-100'
              }`}
            >
              {sub.label}
            </button>
          ))}
        </div>
      </div>

      {/* Filters Header */}
      <div className="bg-white border-b sticky top-16 md:top-20 z-30">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">{pagination.total} accesorios</p>
            </div>
            <div className="flex items-center gap-4">
              <select
                value={filters.sort}
                onChange={(e) => updateFilter('sort', e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm"
              >
                <option value="newest">Más recientes</option>
                <option value="popular">Más populares</option>
                <option value="price_asc">Precio menor</option>
                <option value="price_desc">Precio mayor</option>
              </select>
              <button 
                onClick={() => setShowFilters(!showFilters)}
                className="md:hidden flex items-center gap-2 px-4 py-2 border rounded-full"
              >
                <Filter className="w-4 h-4" /> Filtros
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex gap-6">
          {/* Sidebar Filters */}
          <div className={`${showFilters ? 'block' : 'hidden'} md:block w-64 shrink-0`}>
            <div className="bg-white rounded-xl border p-4 sticky top-48">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold">Filtros</h3>
                <button onClick={clearFilters} className="text-xs text-gray-500 hover:text-red-500">
                  Limpiar
                </button>
              </div>

              <div className="space-y-4">
                {/* Subcategory */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Tipo de Accesorio</label>
                  <select
                    value={filters.subcategory}
                    onChange={(e) => updateFilter('subcategory', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="">Todos</option>
                    {subcategories.map(sub => (
                      <option key={sub.id} value={sub.id}>{sub.label}</option>
                    ))}
                  </select>
                </div>

                {/* Search */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Buscar</label>
                  <input
                    type="text"
                    value={filters.search}
                    onChange={(e) => updateFilter('search', e.target.value)}
                    placeholder="Casco, guantes..."
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  />
                </div>

                {/* Condition */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Condición</label>
                  <div className="space-y-1">
                    {CONDITIONS.map(c => (
                      <label key={c} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="condition"
                          checked={filters.condition === c}
                          onChange={() => updateFilter('condition', c)}
                          className="accent-primary"
                        />
                        <span className="text-sm">{c === 'NEW' ? 'Nuevo' : 'Usado'}</span>
                      </label>
                    ))}
                    {filters.condition && (
                      <button onClick={() => updateFilter('condition', '')} className="text-xs text-gray-500 hover:text-red-500">
                        Limpiar condición
                      </button>
                    )}
                  </div>
                </div>

                {/* Price */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Precio</label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      value={filters.min_price}
                      onChange={(e) => updateFilter('min_price', e.target.value)}
                      placeholder="Mín"
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                    />
                    <input
                      type="number"
                      value={filters.max_price}
                      onChange={(e) => updateFilter('max_price', e.target.value)}
                      placeholder="Máx"
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                    />
                  </div>
                </div>

                {/* Size */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Talla</label>
                  <div className="flex flex-wrap gap-1">
                    {SIZES.map(s => (
                      <button
                        key={s}
                        onClick={() => updateFilter('size', filters.size === s ? '' : s)}
                        className={`px-3 py-1 text-xs rounded border ${
                          filters.size === s ? 'bg-primary text-white border-primary' : 'border-gray-300 hover:border-primary'
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Color */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Color</label>
                  <input
                    type="text"
                    value={filters.color}
                    onChange={(e) => updateFilter('color', e.target.value)}
                    placeholder="Negro, Rojo..."
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  />
                </div>

                {/* Province */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Provincia</label>
                  <select
                    value={filters.province}
                    onChange={(e) => updateFilter('province', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="">Todas</option>
                    {PROVINCES.map(p => (
                      <option key={p} value={p}>{p.replace('_', ' ')}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Products Grid */}
          <div className="flex-1">
            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="bg-gray-100 animate-pulse rounded-xl h-64" />
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-16">
                <span className="text-6xl">🧤</span>
                <h3 className="text-xl font-bold mt-4">No hay accesorios</h3>
                <p className="text-gray-500 mt-2">Prueba con otros filtros</p>
                <button onClick={clearFilters} className="btn-primary mt-4">
                  Limpiar filtros
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {products.map(p => (
                    <a key={p.id} href={`/products/${p.id}`} className="bg-white rounded-xl border overflow-hidden hover:shadow-lg transition">
                      <div className="aspect-square bg-gray-100">
                        {p.images?.[0]?.url ? (
                          <img src={p.images[0].url} alt={p.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-4xl">🧤</div>
                        )}
                      </div>
                      <div className="p-4">
                        <h3 className="font-medium text-sm truncate">{p.title}</h3>
                        <p className="font-bold text-primary mt-1">₡{Number(p.price).toLocaleString()}</p>
                        <p className="text-xs text-gray-500 mt-1">{p.province?.replace('_', ' ')}</p>
                      </div>
                    </a>
                  ))}
                </div>
                
                {pagination.pages > 1 && (
                  <div className="flex justify-center gap-2 mt-8">
                    {[...Array(pagination.pages)].map((_, i) => (
                      <button
                        key={i}
                        onClick={() => updateFilter('page', (i + 1).toString())}
                        className={`w-10 h-10 rounded-full ${
                          pagination.page === i + 1 ? 'bg-primary text-white' : 'bg-gray-100'
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}